import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import './Header.css';

export interface HeaderProps {
  showLogout?: boolean;
  onLogoClick?: () => void;
  /** Whether to use userConfig branding (only in project pages) */
  useProjectBranding?: boolean;
}

export function Header({ showLogout = true, onLogoClick, useProjectBranding = false }: HeaderProps) {
  const navigate = useNavigate();
  const { user, logout, application, isAuthenticated } = useAuth();
  const { userConfig, selectedProject, selectedApplication } = useApp();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Use application from auth or app context
  const currentApp = application || selectedApplication;

  // Only use userConfig branding when inside a project (useProjectBranding=true)
  const appTitle = useProjectBranding && userConfig?.settings?.appTitle
    ? userConfig.settings.appTitle
    : currentApp?._name || 'DT Platform';
  const appImageUrl = useProjectBranding ? userConfig?.settings?.appImage?.url : undefined;

  // Get user initials
  const getUserInitials = () => {
    if (!user) return '?';
    const first = user._firstname?.charAt(0)?.toUpperCase() || '';
    const last = user._lastname?.charAt(0)?.toUpperCase() || '';
    return first + last || user._email?.charAt(0)?.toUpperCase() || '?';
  };

  const getUserFullName = () => {
    if (!user) return 'User';
    if (user._firstname || user._lastname) {
      return `${user._firstname || ''} ${user._lastname || ''}`.trim();
    }
    return user._email || 'User';
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setDropdownOpen(false);
    await logout();
  };

  return (
    <header className="app-header">
      <div className="header-left">
        <div className="header-brand" onClick={onLogoClick} role="button" tabIndex={0}>
          {appImageUrl ? (
            <img src={appImageUrl} alt={appTitle} className="header-logo" />
          ) : (
            <div className="header-logo-placeholder">
              <i className="fas fa-cube"></i>
            </div>
          )}
          <div className="header-titles">
            <span className="header-title">{appTitle}</span>
            {useProjectBranding && currentApp?._name && currentApp._name !== appTitle && (
              <span className="header-app-name">{currentApp._name}</span>
            )}
          </div>
        </div>
        {selectedProject && (
          <div className="header-project">
            <span className="header-separator">/</span>
            <span className="project-name">{selectedProject._name}</span>
          </div>
        )}
      </div>

      <div className="header-right">
        {isAuthenticated && showLogout && (
          <div className="header-user-menu" ref={dropdownRef}>
            <button
              className={`user-avatar-button ${dropdownOpen ? 'active' : ''}`}
              onClick={() => setDropdownOpen(!dropdownOpen)}
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
            >
              <div className="user-avatar">
                {getUserInitials()}
              </div>
              {user && (
                <div className="user-info">
                  <span className="user-name">{getUserFullName()}</span>
                  <span className="user-email">{user._email}</span>
                </div>
              )}
              <i className={`fas fa-chevron-${dropdownOpen ? 'up' : 'down'} dropdown-arrow`}></i>
            </button>

            {dropdownOpen && (
              <div className="user-dropdown">
                <div className="dropdown-header">
                  <div className="dropdown-avatar">
                    {getUserInitials()}
                  </div>
                  <div className="dropdown-user-info">
                    <span className="dropdown-user-name">{getUserFullName()}</span>
                    {user && <span className="dropdown-user-email">{user._email}</span>}
                  </div>
                </div>
                <div className="dropdown-divider"></div>
                  {/*<button className="dropdown-item" onClick={() => { setDropdownOpen(false); navigate('/diagnostics'); }}>
                  <i className="fas fa-microscope"></i>
                  <span>User Management Lab</span>
                </button>*/}
                <div className="dropdown-divider"></div>
                <button className="dropdown-item" onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt"></i>
                  <span>Sign out</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
